import { t } from "i18next";
import { useState } from "react";
import { apiFetchRoute } from "../../../api/apiFetchRoute";
import { enumsectionToI18nKey } from "../../../generated/enums";
import { RequestTypeOf } from "../../../generated/routes";
import { accountLinks } from "../../../api/adminDirectory";
import {
  Account,
  Breeding,
  DeviceWithoutLegacyPassword,
} from "../../../generated/types";
import { useAdminDirectory } from "../../../hooks/useAdminDirectory";
import { isAdminAccount } from "../../../utils/roles";
import { Button } from "../../common/Button";
import { ConfirmModal } from "../../common/ConfirmModal";
import { DeleteModal } from "../../common/DeleteModal";
import { ErrorModal } from "../../common/ErrorModal";
import { SuccessModal } from "../../common/SuccessModal";
import { TableDetails, TicketNote } from "../../common/TableDetails";
import { TitleSection } from "../../common/TitleSection";
import { sectionToIcon } from "../../type/default";
import { BreedingSection } from "../breeding/BreedingSection";
import { BreedingTicket } from "../breeding/BreedingTicket";
import { CreateBreedingModal } from "../breeding/CreateBreedingModal";
import { DeviceSection } from "../device/DeviceSection";
import { DeviceTicket } from "../device/DeviceTicket";
import { CompanySection } from "./CompanySection";
import { UserSection } from "./UserSection";

type UserTicketProps = {
  account: Account;
  onClose: () => void;
  useBackIcon?: boolean;
  /** Rafraîchit la page sous les tickets. */
  onDataRefresh?: () => void;
};

export const UserTicket = ({
  account: initialAccount,
  onClose,
  useBackIcon = true,
  onDataRefresh,
}: UserTicketProps) => {
  const { directory, isLoading, failed, reload } = useAdminDirectory();
  const { account, company, breedings, devices } = accountLinks(
    directory,
    initialAccount
  );
  const refresh = () => {
    reload();
    onDataRefresh?.();
  };
  const fullName = `${account.last_name} ${account.first_name}`;
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedBreeding, setSelectedBreeding] = useState<Breeding | null>(
    null
  );
  const [selectedDevice, setSelectedDevice] =
    useState<DeviceWithoutLegacyPassword | null>(null);

  // Réinitialisation du mot de passe
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
  const [isResetSuccessOpen, setIsResetSuccessOpen] = useState(false);
  const [isResetLoading, setIsResetLoading] = useState(false);

  // Popup creation élevage
  const [isCreateBreedingOpen, setIsCreateBreedingOpen] = useState(false);

  const canManageAccount = isAdminAccount();

  const handleDeleteAccount = async () => {
    try {
      const response = await apiFetchRoute("deleteAccountsId", undefined, {
        params: { id: account.id },
      });

      if (response.ok) {
        setIsDeleteModalOpen(false);
        setIsSuccessModalOpen(true);
        setTimeout(() => {
          setIsSuccessModalOpen(false);
          window.location.reload();
        }, 2000);
      } else {
        setError(t("account-delete-error"));
      }
    } catch (error) {
      console.error("Erreur lors de la suppression du compte:", error);
      setError(t("account-delete-error"));
    }
  };

  const handleCloseSuccessModal = () => {
    setIsSuccessModalOpen(false);
    window.location.reload();
  };

  const handleConfirmResetPassword = async () => {
    setError(null);
    setIsResetLoading(true);
    try {
      type SendEmailRequest = RequestTypeOf<"postSendmailtocreatepassword">;

      const body: SendEmailRequest = { email: account.email };
      const response = await apiFetchRoute(
        "postSendmailtocreatepassword",
        body
      );

      if (response.ok) {
        setIsResetConfirmOpen(false);
        setIsResetSuccessOpen(true);
      } else {
        setError(t("account-reset-password-error"));
      }
    } catch (e) {
      setError(t("account-reset-password-error"));
    } finally {
      setIsResetLoading(false);
    }
  };

  return (
    <div className="flex">
      <div className="flex-1">
        <TableDetails
          title={t(enumsectionToI18nKey["section_account"])}
          icon={sectionToIcon.section_account}
          onClose={onClose}
          onDelete={
            canManageAccount ? () => setIsDeleteModalOpen(true) : undefined
          }
          deleteText={t("delete-account")}
          useBackIcon={useBackIcon}
        >
          <div className="p-8 space-y-4">
            <UserSection user={account} detailLevel="maxi" onUpdate={refresh} />
            {company && (
              <CompanySection company={company} detail onUpdate={refresh} />
            )}

            {isLoading || failed ? (
              <TicketNote>
                {failed
                  ? String(t("ticket-links-error"))
                  : `${String(t("loading"))}…`}
              </TicketNote>
            ) : (
              <>
                <TitleSection
                  title={String(t(enumsectionToI18nKey["section_breedings"]))}
                  icon={sectionToIcon.section_breeding}
                  onClick={() => setIsCreateBreedingOpen(true)}
                />
                {breedings.length > 0 ? (
                  <div className="border-l-2 border-secondary pl-4 ml-2">
                    {breedings.map((breeding) => (
                      <BreedingSection
                        key={breeding.id}
                        breeding={breeding}
                        detailLevel={breedings.length > 3 ? "mini" : "default"}
                        onClick={() => setSelectedBreeding(breeding)}
                      />
                    ))}
                  </div>
                ) : (
                  <TicketNote>{String(t("ticket-no-breeding"))}</TicketNote>
                )}
                <TitleSection
                  title={String(t(enumsectionToI18nKey["section_devices"]))}
                  icon={sectionToIcon.section_device}
                />
                {devices.length > 0 ? (
                  <div className="border-l-2 border-secondary pl-4 ml-2">
                    {devices.map((device) => (
                      <DeviceSection
                        key={device.id}
                        device={device}
                        detailLevel={devices.length > 3 ? "mini" : "default"}
                        onClick={() => setSelectedDevice(device)}
                      />
                    ))}
                  </div>
                ) : (
                  <TicketNote>{String(t("ticket-no-device"))}</TicketNote>
                )}
              </>
            )}
          </div>
          {canManageAccount && (
            <div>
              <Button
                type="button"
                variant="secondary"
                size="full"
                className="mt-2 text-sm"
                onClick={() => setIsResetConfirmOpen(true)}
              >
                {String(t("account-reset-password"))}
              </Button>
            </div>
          )}
        </TableDetails>
      </div>

      {selectedBreeding && (
        <BreedingTicket
          breeding={selectedBreeding}
          onClose={() => setSelectedBreeding(null)}
          onDataRefresh={onDataRefresh}
        />
      )}

      {selectedDevice && (
        <DeviceTicket
          device={selectedDevice}
          onClose={() => setSelectedDevice(null)}
          onDataRefresh={onDataRefresh}
        />
      )}

      <DeleteModal
        title={t("delete-account")}
        warningText={t("account-delete-warning", { name: fullName })}
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        handleConfirmDelete={handleDeleteAccount}
      />

      <SuccessModal
        isOpen={isSuccessModalOpen}
        onClose={handleCloseSuccessModal}
        title={t("account-deleted-title")}
        message={String(t("account-deleted-message"))}
      />

      {/* Confirmation réinitialisation mot de passe */}
      <ConfirmModal
        isOpen={isResetConfirmOpen}
        onCancel={() => setIsResetConfirmOpen(false)}
        onConfirm={handleConfirmResetPassword}
        title={t("account-reset-password")}
        description={
          <div className="space-y-2">
            <p>
              {String(t("account-reset-password-confirm"))}
              <span className="ml-1 font-oxaniumMedium text-tertiary">
                {fullName}
              </span>
              .
            </p>
            <p>
              {String(t("account-reset-password-email-will"))}
              <span className="ml-1 font-oxaniumMedium break-all">
                {account.email}
              </span>
              .
            </p>
          </div>
        }
        loading={isResetLoading}
      />

      {/* Succès réinitialisation mot de passe */}
      <SuccessModal
        isOpen={isResetSuccessOpen}
        onClose={() => setIsResetSuccessOpen(false)}
        title={t("account-reset-password-done-title")}
        message={
          <>
            <span className="font-oxaniumMedium">
              {String(t("account-reset-password-done", { name: fullName }))}
            </span>
            <br />
            <span>
              {String(t("account-reset-password-email-sent"))}
              <span className="ml-1 font-oxaniumMedium break-all">
                {account.email}
              </span>
              .
            </span>
          </>
        }
      />

      <CreateBreedingModal
        companyId={account.id_company}
        companyName={company?.company_name ?? ""}
        accountNames={[fullName]}
        isOpen={isCreateBreedingOpen}
        onClose={() => setIsCreateBreedingOpen(false)}
        onSuccess={refresh}
      />

      {error && (
        <ErrorModal
          isOpen={!!error}
          onClose={() => setError(null)}
          message={error}
        />
      )}
    </div>
  );
};
